import { profileExistsBy, validatePixKey } from '@api/profile';
import { ExternalValidationFunction } from '@interfaces/externalValidationResolver';
import { validatePhone } from '@utils/formValidation/phoneValidation';
import { z } from 'zod';
import { PlatformUserAddress } from './platformUserAddress/platformUserAddress';
import { registerSchema } from './auth';
import { birthDateValidation } from '@utils/formValidation/birthDateValidation';
import { cleanDocument } from '@utils/inputValidation';
import { CommissionRateType } from './_common';

export type PlatformUser = {
  id: number;
  name: string;
  email: string;
  document: string;
  phone: string;
  birthDate: string;
  automaticWithdralEnabled: boolean;
  referrerCode: string;
  referralCode: string;
  referralRate: number;
  pix: string;
  lastProfileUpdateAlertDate: string;
  creationDate: string;
  address: PlatformUserAddress | null;
  preferredName: string | null;
  gender: string | null;
  emailConfirmed: boolean;
  phoneConfirmed: boolean;
};

export type UserBalance = {
  availableBalance: number | null;
  blockedBalance: number | null;
};

export type CreateUserDto = {
  name: string;
  document: string;
  cel: string;
  email: string;
  botConfigId: number;
  telegramUserId: number;
  username: string;
  hash?: string;
};

export const updatePlatformUserSchema = registerSchema
  .pick({
    preferredName: true,
    name: true,
    gender: true,
    genderFreeText: true,
  })
  .merge(
    z.object({
      birthDate: birthDateValidation,
    }),
  );

export type UpdateUserDto = {
  phone?: PlatformUser['phone'];
  pix?: PlatformUser['pix'];
  name?: PlatformUser['name'];
  birthDate?: PlatformUser['birthDate'];
  email?: PlatformUser['email'];
  lastProfileUpdateAlertDate?: PlatformUser['lastProfileUpdateAlertDate'];
  preferredName?: PlatformUser['preferredName'];
  gender?: PlatformUser['gender'];
  genderFreeText?: PlatformUser['gender'];
};

export type ProfileExistsByResponse = { success: boolean };

export type ValidatePixKeyCredentials = {
  pixKey: string;
};

export type ValidatePixKeyResponse = {
  success: boolean;
  msg: string;
  formattedPixKey: string;
};

export type ResetPasswordResponse = { success: boolean; msg: string };

export type ProfileExistsByDto = {
  email?: string;
  document?: string;
};

export const isPixKeyValid: ExternalValidationFunction = async (pixKey) => {
  const {
    success,
    msg: errorMessage,
    formattedPixKey,
  } = await validatePixKey({
    pixKey: pixKey as string,
  });
  return { success, errorMessage, formattedPixKey };
};

export const isExistsByValid =
  (type: keyof ProfileExistsByDto): ExternalValidationFunction =>
  async (value) => {
    const resolveBody = () => {
      switch (type) {
        case 'document':
          return { document: cleanDocument(value) };

        case 'email':
        default:
          return { email: value as string };
      }
    };
    const body = resolveBody();

    const response = await profileExistsBy(body);
    return {
      success: !response.success,
      errorMessage: !response.success ? '' : 'Este usuário já está cadastrado.',
    };
  };

export type _2faStatus = {
  email: string;
  phone: string;
  emailConfirmed: boolean;
  phoneConfirmed: boolean;
  /**
   * Se o suporte já conferiu os documentos. Opcional durante a janela de deploy, enquanto a
   * platform-api#276 não subir. Sem o campo, a etapa fica pendente; o toggle
   * `withdrawalRequest2fa` controla a ativação da exigência.
   */
  documentValidated?: boolean;
};

export const _2faCodeSchema = z.object({
  _2faCode: z.string().length(6, 'Código em formato inválido.'),
});
export type _2faCode = z.infer<typeof _2faCodeSchema>;
export type Validate2faCodeDto = _2faCode;

export const _2faPhoneUpdateSchema = z.object({
  phone: validatePhone(),
});
export type _2faPhoneUpdate = { phone: string };

export const profilePixSchema = z.object({
  pix: z.string().trim().nonempty('A chave pix é obrigatória.'),
});

export type GetInvalidPixKeyResponse = {
  botConfigId: number;
  botConfigName: string;
  botConfigProductCode: string;
  commissionRateType: CommissionRateType;
};

export type UpdatePlatformUserCommissionRatesDto = {
  pixKey: string;
};
